import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { WEEKDAYS } from '../utils/rosterDates';

export default function RosterShiftFormFields({
  track, form, setForm, staffList, editing, toIso, accent, toggleDay, applyPreset,
}) {
  const noun = track.staffNoun;
  return (
    <div className="p-5 space-y-4">
      <div>
        <Label className="text-xs">Quick presets</Label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {(track.presets || []).map((p) => (
            <button key={p.id} type="button" onClick={() => applyPreset(p)}
              className="text-[11px] px-2.5 py-1.5 rounded-full border hover:bg-zinc-50">
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>{track.label.replace(/s$/, '')} *</Label>
        <select
          className="w-full h-10 border rounded-md px-3 text-sm"
          value={form.staff_id}
          onChange={(e) => setForm({ ...form, staff_id: e.target.value })}
          data-testid="roster-staff"
        >
          <option value="">Select {noun}</option>
          {staffList.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}{d.specialization ? ` — ${d.specialization}` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Start</Label>
          <Input type="time" value={form.shift_start}
            onChange={(e) => setForm({ ...form, shift_start: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>End</Label>
          <Input type="time" value={form.shift_end}
            onChange={(e) => setForm({ ...form, shift_end: e.target.value })} />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label>Location</Label>
        <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
      </div>

      {track.bookingAware && (
        <>
          <div className="space-y-1.5">
            <Label>Slot minutes</Label>
            <Input type="number" min={5} max={120} value={form.slot_minutes}
              onChange={(e) => setForm({ ...form, slot_minutes: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Max walk-ins this day</Label>
            <Input
              type="number"
              min={0}
              placeholder="Inherit clinic / doctor"
              value={form.max_walkins ?? ''}
              onChange={(e) => setForm({ ...form, max_walkins: e.target.value })}
              data-testid="roster-max-walkins"
            />
            <p className="text-[11px] text-zinc-500">Blank = inherit · 0 = unlimited · N = cap</p>
          </div>
        </>
      )}

      {!editing && (
        <div className="rounded-xl border bg-zinc-50 p-3 space-y-3">
          <label className="flex items-center gap-2 text-sm font-medium text-zinc-800">
            <input type="checkbox" checked={form.recurring} data-testid="roster-recurring"
              onChange={(e) => setForm({ ...form, recurring: e.target.checked })} />
            Repeat across period (weekly pattern)
          </label>
          {form.recurring && (
            <>
              <div className="flex flex-wrap gap-1.5">
                {WEEKDAYS.map((d) => (
                  <button key={d.v} type="button" onClick={() => toggleDay(d.v)}
                    className={`px-2.5 py-1 rounded-full text-[11px] border ${
                      form.days_of_week.includes(d.v) ? 'text-white border-transparent' : 'bg-white text-zinc-600'
                    }`}
                    style={form.days_of_week.includes(d.v) ? { background: accent } : {}}>
                    {d.l}
                  </button>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label>Until date</Label>
                <Input type="date" value={form.to_date}
                  onChange={(e) => setForm({ ...form, to_date: e.target.value })} />
                <p className="text-[11px] text-zinc-500">Default ends at visible period ({toIso}).</p>
              </div>
            </>
          )}
        </div>
      )}

      <label className="flex items-center gap-2 text-sm text-zinc-700">
        <input type="checkbox" checked={form.published}
          onChange={(e) => setForm({ ...form, published: e.target.checked })} />
        {track.bookingAware
          ? 'Published (visible for online booking)'
          : 'Published (confirmed duty for ops)'}
      </label>
    </div>
  );
}
